import { carregarRascunho, salvarRascunho } from './db';
import type { DraftData, OrcamentoItem, TabelaOrcamento } from './types';
import { atualizarChecklistPlanoFinanceiro, debounceKey } from './progress';
import { setLastEditNow } from './lastedit';

type GrupoOrcamento = { titulo: string; subsecoes: string[] };

const GRUPOS_ORCAMENTO: GrupoOrcamento[] = [
  {
    titulo: '9.1. RECURSOS HUMANOS',
    subsecoes: [
      '9.1.1. RECURSOS HUMANOS DA UNIVERSIDADE',
    ],
  },
  {
    titulo: '9.2. SERVIÇOS DE TERCEIROS PESSOA JURÍDICA',
    subsecoes: [
      '',
    ],
  },
  {
    titulo: '9.3. MATERIAL DE CONSUMO',
    subsecoes: [
      '',
    ],
  },
  {
    titulo: '9.4. MATERIAL PERMANENTE',
    subsecoes: [
      '',
    ],
  },
  {
    titulo: '9.5. OUTRAS DESPESAS',
    subsecoes: [
      '',
    ],
  },
  {
    titulo: '9.6. DESPESAS OPERACIONAIS ADMINISTRATIVAS',
    subsecoes: [
      '',
    ],
  },
];

const TABLES_CONTAINER_SEL = '#tabelas-container';
const TOTAL_GERAL_SEL = '#total-geral';

function el<T extends Element>(sel: string, root: ParentNode | Document = document): T | null {
  return root.querySelector<T>(sel);
}
function els<T extends Element>(sel: string, root: ParentNode | Document = document): T[] {
  return Array.from(root.querySelectorAll<T>(sel));
}

function parseNumberBR(input: string): number {
  if (!input) return 0;
  const s = String(input).trim();

  if (s.includes(',') && s.includes('.')) {
    return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
  }

  if (s.includes(',')) {
    return Number(s.replace(',', '.')) || 0;
  }

  return Number(s.replace(/[^\d.-]/g, '')) || 0;
}

function formatBRL(n: number): string {
  try {
    return (n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  } catch {
    return `R$ ${((n || 0).toFixed(2)).replace('.', ',')}`;
  }
}
function parseBRLLabel(label: string): number {
  if (!label) return 0;
  const digits = label.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
  const n = Number(digits);
  return Number.isFinite(n) ? n : 0;
}

function novaLinha(item?: Partial<OrcamentoItem>): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'table-row';
  row.setAttribute('data-orcamento-row', '1');

  row.innerHTML = `
    <input type="text" class="input-bolsa" placeholder="Descrição" />
    <input type="text" class="input-unidade" placeholder="Unidade" />
    <input type="number" step="1" min="0" class="input-quantidade" placeholder="0" />
    <input type="text" inputmode="decimal" class="input-valor-unitario" placeholder="0,00" />
    <input type="text" class="input-valor-total" placeholder="0,00" disabled />
    <button type="button" class="remove-item">Remover</button>
  `;

  const $bolsa = row.querySelector<HTMLInputElement>('.input-bolsa')!;
  const $unidade = row.querySelector<HTMLInputElement>('.input-unidade')!;
  const $qtd = row.querySelector<HTMLInputElement>('.input-quantidade')!;
  const $vu = row.querySelector<HTMLInputElement>('.input-valor-unitario')!;
  const $vt = row.querySelector<HTMLInputElement>('.input-valor-total')!;
  const $rem = row.querySelector<HTMLButtonElement>('.remove-item')!;

  if (item) {
    $bolsa.value = item.bolsa ?? '';
    $unidade.value = item.unidade ?? '';
    if (typeof item.quantidade === 'number') $qtd.value = String(item.quantidade);
    if (typeof item.valorUnitario === 'number') $vu.value = String(item.valorUnitario).replace('.', ',');
  }

  function recalcLinha() {
    const q = parseNumberBR($qtd.value);
    const vu = parseNumberBR($vu.value);
    const vt = q * vu;
    $vt.value = formatBRL(vt);
    debounceKey('orcamento-sum', () => { void recalcSectionTotalsAndPersist(); }, 120);
  }

  [$bolsa, $unidade, $qtd, $vu].forEach((inp) => {
    inp.addEventListener('input', recalcLinha);
    inp.addEventListener('change', recalcLinha);
  });

  $rem.addEventListener('click', () => {
    row.remove();
    void recalcSectionTotalsAndPersist();
    setLastEditNow();
  });

  recalcLinha();
  return row;
}

type RHItem = {
  nome?: string;
  identificacao?: string;
  email?: string;
  fone?: string;
  funcao?: string;
};

function novaLinhaRH(item?: Partial<RHItem>): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'list-item'; // reaproveita estilo de lista
  row.setAttribute('data-orcamento-row', '1');
  row.setAttribute('data-rh-row', '1');

  row.innerHTML = `
    <input type="text" class="inp-nome" placeholder="Nome completo" />
    <input type="text" class="inp-ident" placeholder="SIAPE/CPF" />
    <input type="email" class="inp-email" placeholder="Email" />
    <input type="text" class="inp-fone" placeholder="Telefone" />
    <input type="text" class="inp-funcao" placeholder="Função" />
    <button type="button" class="remove-button btn btn-danger">Remover</button>
  `;

  const $nome = row.querySelector<HTMLInputElement>('.inp-nome')!;
  const $ident = row.querySelector<HTMLInputElement>('.inp-ident')!;
  const $email = row.querySelector<HTMLInputElement>('.inp-email')!;
  const $fone = row.querySelector<HTMLInputElement>('.inp-fone')!;
  const $func = row.querySelector<HTMLInputElement>('.inp-funcao')!;
  const $rm = row.querySelector<HTMLButtonElement>('.remove-button')!;

  if (item) {
    $nome.value = item.nome ?? '';
    $ident.value = item.identificacao ?? '';
    $email.value = item.email ?? '';
    $fone.value = item.fone ?? '';
    $func.value = item.funcao ?? '';
  }

  const scheduleSave = () => debounceKey(
    'orcamento-rh-change',
    async () => { await recalcSectionTotalsAndPersist(); },
    250
  );

  [$nome, $ident, $email, $fone, $func].forEach((inp) => {
    inp.addEventListener('input', () => {
      scheduleSave();
      setLastEditNow();
    });
    inp.addEventListener('change', () => {
      scheduleSave();
      setLastEditNow();
    });
  });

  $rm.addEventListener('click', () => {
    row.remove();
    void recalcSectionTotalsAndPersist();
    setLastEditNow();
  });

  return row;
}

function novaLinhaRHFin(valor?: any): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'table-row list-item';
  row.setAttribute('data-orcamento-row', '1');

  row.innerHTML = `
  <div class="rhfin-cell rhfin-nome field">
    <label class="field-label">Nome</label>
    <input class="inp-nome" type="text" placeholder="Nome" value="${valor?.nome ?? ''}">
  </div>

  <div class="rhfin-cell rhfin-ident field">
    <label class="field-label">SIAPE/Matrícula</label>
    <input class="inp-ident" type="text" placeholder="SIAPE/Matrícula" value="${valor?.identificacao ?? ''}">
  </div>

  <div class="rhfin-cell rhfin-email field">
    <label class="field-label">Email</label>
    <input class="inp-email" type="email" placeholder="Email" value="${valor?.email ?? ''}">
  </div>

  <div class="rhfin-cell rhfin-fone field">
    <label class="field-label">Telefone</label>
    <input class="inp-fone" type="text" placeholder="Telefone" value="${valor?.fone ?? ''}">
  </div>

  <div class="rhfin-cell rhfin-func field">
    <label class="field-label">Função no projeto</label>
    <input class="inp-funcao" type="text" placeholder="Função no projeto" value="${valor?.funcao ?? ''}">
  </div>

  <div class="rhfin-cell rhfin-horas field">
    <label class="field-label">Horas semanais</label>
    <input
      class="input-horas"
      type="number"
      min="0"
      step="1"
      placeholder="Horas semanais"
      value="${valor?.horas != null ? String(valor.horas) : ''}"
    >
  </div>

  <div class="rhfin-cell rhfin-pagamento field">
    <label class="field-label">Forma de Pagamento</label>
    <input
      class="inp-pagamento"
      type="text"
      placeholder="Forma de Pagamento"
      value="${valor?.formaPagamento ?? ''}"
    >
  </div>

  <div class="rhfin-cell rhfin-qtd field">
    <label class="field-label">Qtd</label>
    <input
      class="input-quantidade"
      type="number"
      min="0"
      step="1"
      placeholder="Qtd"
      value="${valor?.quantidade != null ? String(valor.quantidade) : ''}"
    >
  </div>

  <div class="rhfin-cell rhfin-vu field">
    <label class="field-label">Valor Unitário</label>
    <input
      class="input-valor-unitario"
      type="text"
      placeholder="Valor Unitário"
      value="${valor?.valorUnitario != null ? Number(valor.valorUnitario) : ''}"
    >
  </div>

  <div class="rhfin-cell rhfin-vt field valor-total" data-calc-total>
  <div class="field-label">Valor total (R$)</div>
  <div class="field-value">R$ 0,00</div>
</div>


  <div class="rhfin-cell rhfin-acoes field">
    <label class="field-label">&nbsp;</label>
    <button
      type="button"
      class="btn btn-danger btn-outline btn-sm remove-item"
    >
      Remover
    </button>
  </div>
`;

  const $nome = row.querySelector<HTMLInputElement>('.inp-nome')!;
  const $ident = row.querySelector<HTMLInputElement>('.inp-ident')!;
  const $email = row.querySelector<HTMLInputElement>('.inp-email')!;
  const $fone = row.querySelector<HTMLInputElement>('.inp-fone')!;
  const $func = row.querySelector<HTMLInputElement>('.inp-funcao')!;
  const $qtd = row.querySelector<HTMLInputElement>('.input-quantidade')!;
  const $vu = row.querySelector<HTMLInputElement>('.input-valor-unitario')!;
  const $out = row.querySelector<HTMLDivElement>('[data-calc-total]')!;
  const $rm = row.querySelector<HTMLButtonElement>('.remove-item')!;
  const $horas = row.querySelector<HTMLInputElement>('.input-horas')!;
  const $pag = row.querySelector<HTMLInputElement>('.inp-pagamento')!;


  const scheduleSave = () => {
    debounceKey(
      'orcamento-rh-change',
      async () => {
        await recalcSectionTotalsAndPersist();
      },
      250
    );
  };
  const onAnyChange = () => {
    recalcLine();
    scheduleSave();
    setLastEditNow();
  };

  $vu.addEventListener('input', onAnyChange);
  $qtd.addEventListener('input', onAnyChange);

  [$nome, $ident, $email, $fone, $func, $horas, $pag].forEach((inp) => {
    inp.addEventListener('input', onAnyChange);
    inp.addEventListener('change', onAnyChange);
  });

  const recalcLine = () => {
    const qtd = Number($qtd.value || 0) || 0;
    const vu = parseBRLLabel($vu.value || '0') || 0;
    const total = qtd * vu;
    const $out = row.querySelector<HTMLDivElement>('[data-calc-total] .field-value')!;
    $out.textContent = formatBRL(total);
  };

  // formata moeda e recalcula
  $vu.addEventListener('input', () => {
    // mantém sua digitação, mas recalcula
    recalcLine();
    scheduleSave();
  });

  [$nome, $ident, $email, $fone, $func, $qtd].forEach(inp =>
    inp.addEventListener('input', () => {
      recalcLine();
      scheduleSave();
    })
  );

  $rm.addEventListener('click', () => {
    row.remove();
    void recalcSectionTotalsAndPersist();
    setLastEditNow();
  });

  // inicializa total
  recalcLine();

  return row;
}

function isRowComplete(r: Element): boolean {
  const bolsa = r.querySelector<HTMLInputElement>('.input-bolsa')?.value?.trim();
  const unidade = r.querySelector<HTMLInputElement>('.input-unidade')?.value?.trim();
  const qtd = r.querySelector<HTMLInputElement>('.input-quantidade')?.value?.trim();
  const vu = r.querySelector<HTMLInputElement>('.input-valor-unitario')?.value?.trim();
  return !!(bolsa && unidade && qtd && vu);
}

function getRowInputs(r: Element) {
  return {
    bolsa: r.querySelector<HTMLInputElement>('.input-bolsa')!,
    unidade: r.querySelector<HTMLInputElement>('.input-unidade')!,
    qtd: r.querySelector<HTMLInputElement>('.input-quantidade')!,
    vu: r.querySelector<HTMLInputElement>('.input-valor-unitario')!,
    vt: r.querySelector<HTMLInputElement>('.input-valor-total')!,
  };
}

function novaTabelaDOM(key: string, isOp6 = false, tituloVisivel?: string): HTMLDivElement {
  const wrap = document.createElement('div');
  wrap.className = 'card card-padded mb-16';
  wrap.setAttribute('data-tabela-orc', '1');
  wrap.setAttribute('data-nome-despesa', key);
  if (isOp6) wrap.setAttribute('data-ops6', '1');

  const label = tituloVisivel ?? key;

  const isRHFin = key.trim().startsWith('9.1.1.');

  if (isRHFin) wrap.setAttribute('data-rh-fin', '1');

  if (isOp6) {
    wrap.innerHTML = `
      <div class="form-grid mb-8">
        <div><strong>${label}</strong></div>
        <div></div>
      </div>

      <div class="budget-grid budget-grid-head">
        <div class="th">Descrição</div>
        <div class="th">Percentual (%)</div>
        <div class="th">R$ valor total</div>
      </div>

      <div class="budget-rows" data-rows></div>

      <div class="budget-total">
        <div class="label">Subtotal</div>
        <div class="value subtotal">R$ 0,00</div>
      </div>
    `;

    const rowsWrap = wrap.querySelector<HTMLDivElement>('[data-rows]')!;
    const labels = [
      'Ressarcimento dos Custos Indiretos (RCI)',
      'D.O.A. Fundação',
      'Custos incorridos',
    ];

    labels.forEach(label => {
      const row = document.createElement('div');
      row.className = 'table-row';
      row.setAttribute('data-orcamento-row', '1');
      row.innerHTML = `
        <div><input type="text" value="${label}" data-skip-reset readonly/></div>
        <div><input type="number" data-percent min="0" max="100" step="1" placeholder="0,00"/></div>
        <div class="valor-total" data-calc-total>R$ 0,00</div>
      `;
      rowsWrap.appendChild(row);
    });

    rowsWrap.addEventListener('input', (ev) => {
      const tgt = ev.target as HTMLInputElement | null;
      if (tgt && tgt.matches('[data-percent]')) {
        recalcSubtotalTabela(wrap);
        void recalcSectionTotalsAndPersist();
        setLastEditNow();
      }
    });
    rowsWrap.addEventListener('change', () => {
      recalcSubtotalTabela(wrap);
      void recalcSectionTotalsAndPersist();
      setLastEditNow();
    });

    return wrap;
  }

  if (isRHFin) {
    wrap.innerHTML = `
  <div class="form-grid mb-8">
    <div><strong>${label}</strong></div>
    <div></div>
  </div>

  <div class="metas-grid metas-grid-head--rhfin">
    <div class="th th--nome"></div>
    <div class="th th--ident"></div>
    <div class="th th--func"></div>
    <div class="th th--fone"></div>
    <div class="th th--email"></div>
    <div class="th th--qtd"></div>
    <div class="th th--vu"></div>
    <div class="th th--vt"></div>
    <div class="th th--acoes"></div>
  </div>

  <div class="budget-rows" data-rows></div>

  <div class="actions">
    <button type="button" class="add-item btn btn-outline">+ Adicionar Item</button>
  </div>

  <div class="budget-total">
    <div class="label">Subtotal</div>
    <div class="value subtotal">R$ 0,00</div>
  </div>
`;
    return wrap;
  }

  wrap.innerHTML = `
    <div class="form-grid mb-8">
      <div><strong>${label}</strong></div>
      <div></div>
    </div>

    <div class="budget-grid budget-grid-head">
      <div class="th">Descrição das despesas</div>
      <div class="th">Unidade</div>
      <div class="th">Qtd</div>
      <div class="th">Valor unitário (R$)</div>
      <div class="th">Valor total (R$)</div>
      <div class="th">Ações</div>
    </div>

    <div class="budget-rows" data-rows></div>

    <div class="actions">
      <button type="button" class="add-item btn btn-outline">+ Adicionar Item</button>
    </div>

    <div class="budget-total">
      <div class="label">Subtotal</div>
      <div class="value subtotal">R$ 0,00</div>
    </div>
  `;
  return wrap;
}

function novoGrupoDOM(titulo: string, numero: number, showTotal: boolean = true): HTMLDivElement {
  const g = document.createElement('div');
  g.className = 'card group-card mb-20';

  g.setAttribute('data-grupo-orc', titulo);
  g.setAttribute('data-grupo-num', String(numero));

  g.innerHTML = `
  <div class="group-head mb-6"><h4>${titulo}</h4></div>
  <div class="group-body" data-group-body></div>

  ${showTotal ? `
    <div class="budget-total group-total">
      <div class="label">Total ${numero}</div>
      <div class="value" data-group-subtotal>R$ 0,00</div>
    </div>
  ` : ``}
`;


  return g;
}

function getItensTabela(tabelaEl: Element): any[] {
  const isOp6 = tabelaEl.hasAttribute('data-ops6');
  const rows = els<HTMLDivElement>('[data-orcamento-row]', tabelaEl);

  const isRHFin = tabelaEl.hasAttribute('data-rh-fin');

  if (isRHFin) {
    return rows.map((r) => {
      const qtd = Number(r.querySelector<HTMLInputElement>('.input-quantidade')?.value || 0) || 0;
      const vu = parseBRLLabel(r.querySelector<HTMLInputElement>('.input-valor-unitario')?.value || '0') || 0;

      const nome = r.querySelector<HTMLInputElement>('.inp-nome')?.value?.trim() || '';

      return {
        bolsa: nome,
        nome,
        identificacao: r.querySelector<HTMLInputElement>('.inp-ident')?.value?.trim() || '',
        email: r.querySelector<HTMLInputElement>('.inp-email')?.value?.trim() || '',
        fone: r.querySelector<HTMLInputElement>('.inp-fone')?.value?.trim() || '',
        funcao: r.querySelector<HTMLInputElement>('.inp-funcao')?.value?.trim() || '',
        horas: Number(r.querySelector<HTMLInputElement>('.input-horas')?.value || 0) || 0,
        formaPagamento: r.querySelector<HTMLInputElement>('.inp-pagamento')?.value?.trim() || '',
        quantidade: qtd,
        valorUnitario: vu,
        valorTotal: qtd * vu,
      };
    });
  }

  if (isOp6) {
    const base = computeSubtotalBase(document);
    return rows.map(r => {
      const desc = r.querySelector<HTMLInputElement>('[data-skip-reset]')!.value.trim();
      const percent = Number((r.querySelector<HTMLInputElement>('[data-percent]')!.value || ''));
      const unit = base / 100;
      return {
        bolsa: desc,
        unidade: '%',
        quantidade: percent,
        valorUnitario: unit,
        valorTotal: unit * percent,
      };
    });
  }

  return rows
    .filter(isRowComplete)
    .map((r) => {
      const { bolsa, unidade, qtd, vu } = getRowInputs(r);
      const quantidade = parseNumberBR(qtd.value);
      const valorUnitario = parseNumberBR(vu.value);
      return {
        bolsa: bolsa.value.trim(),
        unidade: unidade.value.trim(),
        quantidade,
        valorUnitario,
        valorTotal: quantidade * valorUnitario,
      };
    });
}

function recalcSubtotalTabela(tabelaEl: Element) {
  const isOp6 = tabelaEl.hasAttribute('data-ops6');
  const isRHFin = tabelaEl.hasAttribute('data-rh-fin');

  if (isRHFin) {
    const itens = getItensTabela(tabelaEl) as any[];
    const subtotal = itens.reduce(
      (acc, it) => acc + (Number(it.quantidade) * Number(it.valorUnitario || 0)),
      0
    );
    const subEl = tabelaEl.querySelector<HTMLDivElement>('.subtotal');
    if (subEl) subEl.textContent = formatBRL(subtotal);
    return subtotal;
  }

  if (isOp6) {
    const base = computeSubtotalBase(document);
    const rows = els<HTMLDivElement>('[data-orcamento-row]', tabelaEl);
    let subtotal = 0;

    rows.forEach(r => {
      const inp = r.querySelector<HTMLInputElement>('[data-percent]');
      const out = r.querySelector<HTMLDivElement>('[data-calc-total]');
      const p = Number((inp?.value || '0')) || 0;
      const val = base * (p / 100);
      if (out) out.textContent = formatBRL(val);
      subtotal += val;
    });

    const subEl = tabelaEl.querySelector<HTMLDivElement>('.subtotal');
    if (subEl) subEl.textContent = formatBRL(subtotal);
    return subtotal;
  }

  const itens = getItensTabela(tabelaEl);
  const subtotal = itens.reduce(
    (acc, it) => acc + (Number(it.quantidade) * Number(it.valorUnitario || 0)),
    0
  );
  const subEl = tabelaEl.querySelector<HTMLDivElement>('.subtotal');
  if (subEl) subEl.textContent = formatBRL(subtotal);
  return subtotal;
}

function recalcAllGroupTotals(root: ParentNode | Document = document) {
  const groups = els<HTMLDivElement>('[data-grupo-orc]', root);
  groups.forEach((g) => {
    const subtotais = els<HTMLDivElement>('.subtotal', g);
    const total = subtotais.reduce((acc, s) => acc + parseBRLLabel(s.textContent || ''), 0);
    const holder = g.querySelector<HTMLDivElement>('[data-group-subtotal]');
    if (holder) holder.textContent = formatBRL(total);
  });
}

function computeSubtotalBase(root: ParentNode | Document = document): number {
  const grupos = Array.from(root.querySelectorAll<HTMLDivElement>('[data-grupo-orc]'));
  return grupos
    .filter(g => {
      const nStr = g.getAttribute('data-grupo-num') || '0';
      const numero = parseInt(nStr, 10);
      return numero >= 1 && numero <= 5; // seções 1 a 5
    })
    .reduce((acc, g) => {
      const val = g.querySelector<HTMLDivElement>('[data-group-subtotal]')?.textContent || 'R$ 0,00';
      return acc + (parseBRLLabel(val) || 0);
    }, 0);
}

function ensureSubtotalCard(container: HTMLElement) {
  if (container.querySelector('#subtotal-base')) return;

  const card = document.createElement('div');
  card.className = 'budget-total';
  card.innerHTML = `
    <div class="label">Sub-Total (Seções 1 a 5)</div>
    <div class="value" id="subtotal-base">R$ 0,00</div>
  `;
  container.appendChild(card);
}

function updateSubtotalBase(root: ParentNode | Document = document) {
  const el = document.querySelector<HTMLDivElement>('#subtotal-base');
  if (!el) return;
  el.textContent = formatBRL(computeSubtotalBase(root));
}

async function recalcSectionTotalsAndPersist() {
  const container = el<HTMLDivElement>(TABLES_CONTAINER_SEL);
  const tabelas = container ? els<HTMLDivElement>('[data-tabela-orc]', container) : [];
  let totalGeral = 0;

  const tabelasBase = tabelas.filter(t => !t.hasAttribute('data-ops6'));
  tabelasBase.forEach((t) => {
    totalGeral += recalcSubtotalTabela(t);
  });

  updateSubtotalBase(container || document);

  const tabelasOp6 = tabelas.filter(t => t.hasAttribute('data-ops6'));
  tabelasOp6.forEach((t) => {
    totalGeral += recalcSubtotalTabela(t);
  });

  const $totalGeral = el<HTMLDivElement>(TOTAL_GERAL_SEL);
  const totalGeralFmt = formatBRL(totalGeral);
  if ($totalGeral) $totalGeral.textContent = totalGeralFmt;

  const temItem = totalGeral > 0;
  atualizarChecklistPlanoFinanceiro(temItem);

  const atual: Partial<DraftData> = (await carregarRascunho()) ?? {};
  const tabelas_orcamento: TabelaOrcamento[] = tabelas.map((t) => {
    const nome = (t.getAttribute('data-nome-despesa') || '').trim();
    const isRHFin = t.hasAttribute('data-rh-fin');
    const itensCalc = getItensTabela(t);

    const subtotalText = t.querySelector<HTMLDivElement>('.subtotal')?.textContent || 'R$ 0,00';
    const tituloGrupo = t.closest('[data-grupo-orc]')?.getAttribute('data-grupo-orc') || undefined;

    return {
      id: '',
      nomeDespesa: nome,
      ...(tituloGrupo ? { tituloGrupo } : {}),
      itens: (isRHFin
        ? itensCalc
        : itensCalc.map(it => ({
          bolsa: it.bolsa,
          unidade: it.unidade,
          quantidade: it.quantidade,
          valorUnitario: it.valorUnitario,
          valorTotal: it.valorTotal,
        }))
      ) as any,
      subtotal: subtotalText,
    } as any as TabelaOrcamento;
  });

  const novo: Partial<DraftData> = {
    ...atual,
    tabelas_orcamento,
    nomeDespesa: tabelas_orcamento[0]?.nomeDespesa,
    orcamento: (tabelas_orcamento[0]?.itens as any) ?? [],
    totalOrcamento: totalGeral,
  };
  await salvarRascunho(novo as DraftData);

  recalcAllGroupTotals(container || document);
}

export async function initOrcamento() {
  const container = el<HTMLDivElement>(TABLES_CONTAINER_SEL);
  if (!container) return;

  const atual: Partial<DraftData> = (await carregarRascunho()) ?? {};

  const makeKeyFromTabela = (t: TabelaOrcamento) =>
    `${t.tituloGrupo ?? ''}::${t.nomeDespesa ?? ''}`;

  const makeKeyFromGrupoSub = (grupoTitulo: string, sub: string) =>
    `${grupoTitulo ?? ''}::${sub ?? ''}`;

  const byKey = new Map<string, OrcamentoItem[]>();
  (atual.tabelas_orcamento || []).forEach((t) => {
    byKey.set(makeKeyFromTabela(t), t.itens || []);
  });

  GRUPOS_ORCAMENTO.forEach((gr, idxGrupo) => {
    const numeroGrupo = idxGrupo + 1;
    const showTotal = true;
    const gDOM = novoGrupoDOM(gr.titulo, numeroGrupo, showTotal);

    const gBody = gDOM.querySelector<HTMLDivElement>('[data-group-body]')!;

    gr.subsecoes.forEach((nomeSub) => {
      const bloco = novaTabelaDOM(nomeSub, numeroGrupo === 6);
      const rowsWrap = bloco.querySelector<HTMLDivElement>('[data-rows]')!;

      const key = makeKeyFromGrupoSub(gr.titulo, nomeSub);
      const itens = byKey.get(key) || [];
      const isOp6 = bloco.hasAttribute('data-ops6');
      const isRHFin = bloco.hasAttribute('data-rh-fin');

      if (isOp6) {
        const rows = Array.from(
          bloco.querySelectorAll<HTMLDivElement>('[data-orcamento-row]')
        );
        itens.forEach((it, idx) => {
          const row = rows[idx];
          if (!row) return;
          const inp = row.querySelector<HTMLInputElement>('[data-percent]');
          if (inp) inp.value = (Number(it.quantidade) || '').toString();
        });
      } else {
        if (isRHFin) {
          if (itens.length) {
            itens.forEach((it: any) => rowsWrap.appendChild(novaLinhaRHFin(it)));
          } else {
            rowsWrap.appendChild(novaLinhaRHFin());
          }

          const btn = bloco.querySelector<HTMLButtonElement>('.add-item');
          if (btn) {
            btn.addEventListener('click', () => {
              rowsWrap.appendChild(novaLinhaRHFin());
              void recalcSectionTotalsAndPersist();
              setLastEditNow();
            });
          }

        } else {
          // tabela normal (financeira)
          if (itens.length) {
            itens.forEach((it) => rowsWrap.appendChild(novaLinha(it)));
          } else {
            rowsWrap.appendChild(novaLinha());
          }

          const btn = bloco.querySelector<HTMLButtonElement>('.add-item');
          if (btn) {
            btn.addEventListener('click', () => {
              rowsWrap.appendChild(novaLinha());
              void recalcSectionTotalsAndPersist();
              setLastEditNow();
            });
          }
        }
      }

      gBody.appendChild(bloco);
      recalcSubtotalTabela(bloco);
    });

    if (numeroGrupo === 6) {
      ensureSubtotalCard(container);
      updateSubtotalBase(container);
    }

    container.appendChild(gDOM);
  });

  recalcAllGroupTotals(container);
  await recalcSectionTotalsAndPersist();

  container.addEventListener('input', () => {
    recalcAllGroupTotals(container);
    setLastEditNow();
  });
  container.addEventListener('change', () => {
    recalcAllGroupTotals(container);
    setLastEditNow();
  });
}


export function getOrcamento(): OrcamentoItem[] {
  const container = el<HTMLDivElement>(TABLES_CONTAINER_SEL);
  const firstTable = container?.querySelector('[data-tabela-orc]');
  if (!firstTable) return [];
  return getItensTabela(firstTable);
}

export function getTotalOrcamento(): number {
  const container = el<HTMLDivElement>(TABLES_CONTAINER_SEL);
  const firstTable = container?.querySelector('[data-tabela-orc]');
  if (!firstTable) return 0;

  const itens = getItensTabela(firstTable);
  return itens.reduce(
    (acc, it) => acc + (Number(it.quantidade) * Number(it.valorUnitario || 0)),
    0
  );
}